import { useAsyncState, type UseAsyncStateOptions } from '@vueuse/core';
import type { MColor } from '@mts/components/Base';
import type { RequestOptions } from '@mts-fe/types';
import type { AxiosRequestConfig } from 'axios';
import { parseRequestMethod, replaceUrl, RequestMethod } from '@mts-fe/utils';
import { BACKEND_SERVICES, SERVICE_PREFIX } from '@mts/utils';
import { IS_LOWCODE } from '@mts/utils/env';
import { defHttp } from '@mts/utils/http/axios';
import type { BaseApi, PageConfig } from '..';
import { DICT_CACHED_MAP, DICT_FETCHED_MAP } from './worker';

/** 创建通用接口请求 */
export const createApi = <T extends Recordable, U = any>(
  url: string,
  method: RequestMethod = RequestMethod.POST,
  config?: AxiosRequestConfig,
  options?: RequestOptions,
) => {
  return async (params: T = {} as T) =>
    defHttp.request<U>(
      { ...config, method, url: params ? replaceUrl(url, params.data || params) : url, params },
      options,
    );
};

/** 获取低代码平台模块配置 */
export const getCFConfigDetail = createApi<
  { pageCode: string; version?: number },
  PageConfig | void
>(SERVICE_PREFIX.LCP + '/configfactory/page/detailByCode');

/** 批量获取低代码平台模块配置 */
export const getCFConfigDetailByList = createApi<{ pageCodeList: string[] }, PageConfig[]>(
  SERVICE_PREFIX.LCP + '/configfactory/page/batchDetailByCode',
);

export interface DictOptionParams {
  modelFieldId?: string;
  dataTypeCode?: string;
  search?: Recordable;
  filter?: { fieldDataCodes?: string[] };
}
export interface DictOption {
  value: string;
  label: string;
  code?: string;
  name?: string;
  color?: MColor;
  multiLangValue?: string;
  disabled?: boolean;
  class?: string;
}
/** 通用数据字典接口 */
export const DICT_OPTION_API = SERVICE_PREFIX.LC + '/dataTypeClassification/commonPullDownList';
const fetchDictOptions = createApi<DictOptionParams, DictOption[]>(DICT_OPTION_API);
/** 获取通用字典下拉 */
export const getDictOptions = async (params: DictOptionParams) => {
  if (params.dataTypeCode && (!params.search || !Object.keys(params.search).length)) {
    const data = DICT_CACHED_MAP[params.dataTypeCode];
    if (data) {
      const { fieldDataCodes } = params.filter || {};
      const dict = fieldDataCodes
        ? data.filter((i) => fieldDataCodes.find((code) => code.toString() === i.value))
        : data;

      console.debug('🚀 DICT FROM CACHE >>', { ...params });
      return Object.freeze(dict) as DictOption[];
    }
  }

  const fetchKey = JSON.stringify(params);
  const fetchedData = DICT_FETCHED_MAP[fetchKey];
  if (fetchedData?.length) {
    console.debug('🚀 DICT FROM FETCHED >>', { ...params });
    return Object.freeze(fetchedData) as DictOption[];
  }

  const res = await fetchDictOptions(params);
  DICT_FETCHED_MAP[fetchKey] = res;
  return Object.freeze(res) as DictOption[];
};

/** 获取字典集合 */
export const useDictOptions = (
  params: DictOptionParams,
  options?: UseAsyncStateOptions<true, DictOption[]>,
  formatOptions?: (options: DictOption[]) => DictOption[],
) => {
  const res = useAsyncState(
    getDictOptions(params).then((res) => formatOptions?.(res) || res),
    [],
    { resetOnExecute: false, delay: 4, ...options },
  );
  return { ...res, options: res.state };
};

/** 获取部门组别下拉项 */
export const getDeptOptions = (
  search: {
    deptType?: 'group' | 'workshop';
    deptTypeList?: ('group' | 'workshop')[];
    /** 工段 */
    sectionTypeCode?:
      | 'cutting_pre'
      | 'cutting'
      | 'sewing'
      | 'sewing_pre'
      | 'sortation'
      | 'after_finishing';
    /** 父级ID */
    pid?: string;
  },
  filter?: DictOptionParams['filter'],
) => getDictOptions({ dataTypeCode: 'sql/sys_dept', search, filter });

const modelApiCache = new Map<string, any>();
/**
 * 定制业务中生成低代码服务的标准接口请求
 * @example
 * createModelApi('ims-system', 'detail')({ modelId: 'xxx', data: {} });
 */
export const createModelApi = <T extends Recordable, U = any>(
  /** 后端的微服务名 */
  applicationName: (typeof BACKEND_SERVICES)[number],
  /** 方法名 */
  method:
    | 'page'
    | 'detail'
    | 'removeById'
    | 'saveOrUpdate'
    | 'save'
    | 'update'
    | 'list'
    | 'batchSave'
    | 'batchSaveOrUpdate'
    | 'detail/concatenated'
    | 'detail/batch'
    | 'exportPageToExcel'
    | 'exportDetailToExcel',
) => {
  const cacheKey = `${applicationName}@${method}`;

  if (modelApiCache.has(cacheKey)) return modelApiCache.get(cacheKey);

  const result = createApi<T, U>(`/${applicationName}/baseModelRun/${method}`);
  modelApiCache.set(cacheKey, result);
  return result;
};

const urlPrefix = IS_LOWCODE ? SERVICE_PREFIX.LCC : SERVICE_PREFIX.LCIC;
/** 获取打印模版配置 */
export const getPrintTemplate = async (id: string): Promise<Recordable> => {
  const res = await createApi(urlPrefix + '/print/template/info')({ id });
  if (!res.jsonSchema) {
    throw new Error('打印模版配置不存在');
  }
  const template = JSON.parse(res.jsonSchema);
  return template;
};

const apiCache = new Map<string, any>();
/** @private 渲染器内：字符串转接口 */
export const createSchemaApi = (
  api?: BaseApi,
  config?: AxiosRequestConfig,
  options?: RequestOptions,
) => {
  if (!api) return api;

  if (typeof api === 'function') {
    return api;
  }

  if (apiCache.has(api)) return apiCache.get(api);
  const result = createApi(...parseRequestMethod(api), config, options);
  apiCache.set(api, result);
  return result;
};
